import React, { useState } from 'react'
import './MemoryGallery.css'

interface Memory {
  id: number
  title: string
  date: string
  description: string
  emotion: string
  tags: string[]
  image: string
  likes: number
  comments: Comment[]
}

interface Comment {
  id: number
  author: string
  content: string
  date: string
}

const MemoryGallery: React.FC = () => {
  const [memories, setMemories] = useState<Memory[]>([
    {
      id: 1,
      title: '第一次约会',
      date: '2024-01-15',
      description: '在咖啡厅里的美好时光，你点了拿铁，我点了卡布奇诺。那时候还有些紧张，但是聊得很开心。',
      emotion: '开心',
      tags: ['约会', '咖啡厅', '初次'],
      image: '☕',
      likes: 12,
      comments: [
        { id: 1, author: '小明', content: '好甜蜜啊！', date: '2024-01-16' },
        { id: 2, author: '小红', content: '记得那天你很紧张呢', date: '2024-01-16' }
      ]
    },
    {
      id: 2,
      title: '一起看电影',
      date: '2024-01-20',
      description: '看了一部浪漫喜剧，你笑得很开心。电影院里昏暗的灯光，我们分享着爆米花。',
      emotion: '浪漫',
      tags: ['电影', '约会', '浪漫'],
      image: '🎬',
      likes: 8,
      comments: [
        { id: 3, author: '小明', content: '那部电影真的很好看', date: '2024-01-21' }
      ]
    },
    {
      id: 3,
      title: '海边散步',
      date: '2024-01-25',
      description: '夕阳西下，我们手牵手在海边走着。海风轻抚，浪花拍岸，那一刻觉得时间都停止了。',
      emotion: '温馨',
      tags: ['海边', '散步', '夕阳'],
      image: '🌊',
      likes: 15,
      comments: []
    }
  ])

  const [selectedMemory, setSelectedMemory] = useState<Memory | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [editingMemory, setEditingMemory] = useState<Memory | null>(null)
  const [filterEmotion, setFilterEmotion] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [uploadedImages, setUploadedImages] = useState<string[]>([])

  const emotions = ['开心', '浪漫', '温馨', '激动', '感动', '幸福']

  const filteredMemories = memories.filter(memory => {
    const matchesEmotion = !filterEmotion || memory.emotion === filterEmotion
    const matchesSearch = !searchTerm || 
      memory.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      memory.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      memory.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    return matchesEmotion && matchesSearch
  })

  const handleLike = (memoryId: number) => {
    setMemories(prev => prev.map(memory => 
      memory.id === memoryId 
        ? { ...memory, likes: memory.likes + 1 }
        : memory
    ))
  }

  const handleAddComment = (memoryId: number, content: string) => {
    const newComment: Comment = {
      id: Date.now(),
      author: '我',
      content,
      date: new Date().toISOString().split('T')[0]
    }
    
    setMemories(prev => prev.map(memory => 
      memory.id === memoryId 
        ? { ...memory, comments: [...memory.comments, newComment] }
        : memory
    ))
  }

  const handleShare = (memoryId: number) => {
    const memory = memories.find(m => m.id === memoryId)
    if (memory) {
      if (navigator.share) {
        navigator.share({
          title: memory.title,
          text: memory.description,
          url: window.location.href
        })
      } else {
        // 复制到剪贴板作为备选方案
        const shareText = `${memory.title}\n${memory.description}\n${window.location.href}`
        navigator.clipboard.writeText(shareText).then(() => {
          alert('分享内容已复制到剪贴板！')
        })
      }
    }
  }

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files) {
      const newImages: string[] = []
      Array.from(files).forEach(file => {
        const reader = new FileReader()
        reader.onload = (e) => {
          if (e.target?.result) {
            newImages.push(e.target.result as string)
            if (newImages.length === files.length) {
              setUploadedImages(prev => [...prev, ...newImages])
            }
          }
        }
        reader.readAsDataURL(file)
      })
    }
  }

  const handleAddMemory = (formData: FormData) => {
    const title = formData.get('title') as string
    const date = formData.get('date') as string
    const description = formData.get('description') as string
    const emotion = formData.get('emotion') as string
    const tagsInput = formData.get('tags') as string
    const tags = tagsInput ? tagsInput.split(',').map(tag => tag.trim()).filter(tag => tag) : []
    
    const newMemory: Memory = {
      id: Date.now(),
      title,
      date,
      description,
      emotion,
      tags,
      image: uploadedImages[0] || '📷',
      likes: 0,
      comments: []
    }
    
    setMemories(prev => [newMemory, ...prev])
    setShowAddForm(false)
    setUploadedImages([])
  }

  const handleEditMemory = (memory: Memory) => {
    setEditingMemory(memory)
    setShowEditForm(true)
  }

  const handleUpdateMemory = (formData: FormData) => {
    if (!editingMemory) return
    
    const title = formData.get('title') as string
    const date = formData.get('date') as string
    const description = formData.get('description') as string
    const emotion = formData.get('emotion') as string
    const tagsInput = formData.get('tags') as string
    const tags = tagsInput ? tagsInput.split(',').map(tag => tag.trim()).filter(tag => tag) : []
    
    const updatedMemory: Memory = {
      ...editingMemory,
      title,
      date,
      description,
      emotion,
      tags,
      image: uploadedImages[0] || editingMemory.image
    }
    
    setMemories(prev => prev.map(memory => 
      memory.id === editingMemory.id ? updatedMemory : memory
    ))
    setShowEditForm(false)
    setEditingMemory(null)
    setUploadedImages([])
  }

  const handleDeleteMemory = (memoryId: number) => {
    if (window.confirm('确定要删除这个回忆吗？')) {
      setMemories(prev => prev.filter(memory => memory.id !== memoryId))
    }
  }

  return (
    <div className="memory-gallery">
      <div className="gallery-header">
        <h2 className="gallery-title">回忆相册 📸</h2>
        <button 
          className="btn btn-primary add-memory-btn"
          onClick={() => setShowAddForm(true)}
        >
          ➕ 添加回忆
        </button>
      </div>

      <div className="gallery-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="搜索回忆..."
            className="input search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="emotion-filters">
          <button 
            className={`filter-btn ${!filterEmotion ? 'active' : ''}`}
            onClick={() => setFilterEmotion('')}
          >
            全部
          </button>
          {emotions.map(emotion => (
            <button
              key={emotion}
              className={`filter-btn ${filterEmotion === emotion ? 'active' : ''}`}
              onClick={() => setFilterEmotion(emotion)}
            >
              {emotion}
            </button>
          ))}
        </div>
      </div>

      <div className="memories-grid">
        {filteredMemories.map(memory => (
          <div key={memory.id} className="memory-card">
            <div className="memory-image-container">
              {memory.image.startsWith('data:') ? (
                <img src={memory.image} alt={memory.title} className="memory-image" />
              ) : (
                <div className="memory-image-placeholder">{memory.image}</div>
              )}
              <div className="memory-emotion-badge">{memory.emotion}</div>
            </div>
            
            <div className="memory-content">
              <h3 className="memory-title">{memory.title}</h3>
              <p className="memory-date">{memory.date}</p>
              <p className="memory-description">{memory.description}</p>
              
              <div className="memory-tags">
                {memory.tags.map(tag => (
                  <span key={tag} className="tag">#{tag}</span>
                ))}
              </div>
              
              <div className="memory-actions">
                <button 
                  className="action-btn like-btn"
                  onClick={() => handleLike(memory.id)}
                >
                  ❤️ {memory.likes}
                </button>
                <button 
                  className="action-btn comment-btn"
                  onClick={() => setSelectedMemory(memory)}
                >
                  💬 {memory.comments.length}
                </button>
                <button 
                  className="action-btn share-btn"
                  onClick={() => handleShare(memory.id)}
                >
                  📤 分享
                </button>
                <button 
                  className="action-btn edit-btn"
                  onClick={() => handleEditMemory(memory)}
                >
                  ✏️ 编辑
                </button>
                <button 
                  className="action-btn delete-btn"
                  onClick={() => handleDeleteMemory(memory.id)}
                >
                  🗑️ 删除
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedMemory && (
        <div className="modal-overlay" onClick={() => setSelectedMemory(null)}>
          <div className="memory-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedMemory.title}</h3>
              <button 
                className="close-btn"
                onClick={() => setSelectedMemory(null)}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <div className="memory-detail-image">{selectedMemory.image}</div>
              <p className="memory-detail-description">{selectedMemory.description}</p>
              
              <div className="comments-section">
                <h4>评论 ({selectedMemory.comments.length})</h4>
                <div className="comments-list">
                  {selectedMemory.comments.map(comment => (
                    <div key={comment.id} className="comment-item">
                      <div className="comment-author">{comment.author}</div>
                      <div className="comment-content">{comment.content}</div>
                      <div className="comment-date">{comment.date}</div>
                    </div>
                  ))}
                </div>
                
                <div className="add-comment">
                  <input
                    type="text"
                    placeholder="写下你的想法..."
                    className="input comment-input"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                        handleAddComment(selectedMemory.id, e.currentTarget.value)
                        e.currentTarget.value = ''
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddForm && (
        <div className="modal-overlay" onClick={() => setShowAddForm(false)}>
          <div className="add-memory-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>添加新回忆</h3>
              <button 
                className="close-btn"
                onClick={() => setShowAddForm(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <form className="add-memory-form" onSubmit={(e) => {
                e.preventDefault()
                const formData = new FormData(e.currentTarget)
                handleAddMemory(formData)
              }}>
                <div className="form-group">
                  <label>上传图片</label>
                  <div className="image-upload-area">
                    <input
                      type="file"
                      id="image-upload"
                      name="images"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="image-input"
                    />
                    <label htmlFor="image-upload" className="upload-label">
                      <span className="upload-icon">📷</span>
                      <span className="upload-text">
                        {uploadedImages.length > 0 ? `已选择 ${uploadedImages.length} 张图片` : '点击上传图片'}
                      </span>
                    </label>
                    {uploadedImages.length > 0 && (
                      <div className="image-preview">
                        {uploadedImages.map((image, index) => (
                          <img key={index} src={image} alt={`预览 ${index + 1}`} className="preview-image" />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="form-group">
                  <label>标题</label>
                  <input type="text" name="title" className="input" placeholder="给这个回忆起个名字..." required />
                </div>
                
                <div className="form-group">
                  <label>日期</label>
                  <input type="date" name="date" className="input" defaultValue={new Date().toISOString().split('T')[0]} required />
                </div>
                
                <div className="form-group">
                  <label>描述</label>
                  <textarea name="description" className="textarea" placeholder="描述这个美好的回忆..." required></textarea>
                </div>
                
                <div className="form-group">
                  <label>情感标签</label>
                  <select name="emotion" className="input" required>
                    <option value="">选择情感</option>
                    {emotions.map(emotion => (
                      <option key={emotion} value={emotion}>{emotion}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>标签</label>
                  <input type="text" name="tags" className="input" placeholder="用逗号分隔多个标签" />
                </div>
                
                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => {
                    setShowAddForm(false)
                    setUploadedImages([])
                  }}>
                    取消
                  </button>
                  <button type="submit" className="btn btn-primary">
                    保存回忆
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showEditForm && editingMemory && (
        <div className="modal-overlay" onClick={() => setShowEditForm(false)}>
          <div className="add-memory-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>编辑回忆</h3>
              <button 
                className="close-btn"
                onClick={() => setShowEditForm(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <form className="add-memory-form" onSubmit={(e) => {
                e.preventDefault()
                const formData = new FormData(e.currentTarget)
                handleUpdateMemory(formData)
              }}>
                <div className="form-group">
                  <label>上传图片</label>
                  <div className="image-upload-area">
                    <input
                      type="file"
                      id="edit-image-upload"
                      name="images"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="image-input"
                    />
                    <label htmlFor="edit-image-upload" className="upload-label">
                      <span className="upload-icon">📷</span>
                      <span className="upload-text">
                        {uploadedImages.length > 0 ? `已选择 ${uploadedImages.length} 张图片` : '点击上传新图片'}
                      </span>
                    </label>
                    {uploadedImages.length > 0 && (
                      <div className="image-preview">
                        {uploadedImages.map((image, index) => (
                          <img key={index} src={image} alt={`预览 ${index + 1}`} className="preview-image" />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="form-group">
                  <label>标题</label>
                  <input type="text" name="title" className="input" defaultValue={editingMemory.title} required />
                </div>
                
                <div className="form-group">
                  <label>日期</label>
                  <input type="date" name="date" className="input" defaultValue={editingMemory.date} required />
                </div>
                
                <div className="form-group">
                  <label>描述</label>
                  <textarea name="description" className="textarea" defaultValue={editingMemory.description} required></textarea>
                </div>
                
                <div className="form-group">
                  <label>情感标签</label>
                  <select name="emotion" className="input" defaultValue={editingMemory.emotion} required>
                    <option value="">选择情感</option>
                    {emotions.map(emotion => (
                      <option key={emotion} value={emotion}>{emotion}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>标签</label>
                  <input type="text" name="tags" className="input" defaultValue={editingMemory.tags.join(', ')} />
                </div>
                
                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => {
                    setShowEditForm(false)
                    setEditingMemory(null)
                    setUploadedImages([])
                  }}>
                    取消
                  </button>
                  <button type="submit" className="btn btn-primary">
                    更新回忆
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MemoryGallery